import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useAxios } from '@/composables/useAxios'
import { K8S_PAYG_DEFAULTS } from '@/constants/calculatorPricing'
import { PricingRatesResponse } from '@/types/pricing/PricingRates'
import type { PaygRates } from '@/utils/termSwitch'

/**
 * The net pay-as-you-go rates of a Kubernetes namespace from the public rates,
 * fetched once per session and shared by every card and dialog showing them.
 * The published defaults stand until they load, and a failed request is
 * retried by the next caller.
 */
export const usePaygRatesStore = defineStore('paygRates', () => {
  const rates = ref<PaygRates>({ ...K8S_PAYG_DEFAULTS })
  let request: Promise<void> | null = null

  function load(): Promise<void> {
    request ??= useAxios<never, PricingRatesResponse>('GET', '/pricing/rates', false, undefined, PricingRatesResponse)
      .then((res) => {
        const svc = res?.data?.services.find((s) => s.service === 'k8s')
        if (!svc) return
        const rate = Object.fromEntries(svc.unit_rates.map((r) => [r.metric, r.price_cents]))
        rates.value = {
          cpuCoreHourCents: rate['cpu_core_hour'] ?? K8S_PAYG_DEFAULTS.cpuCoreHourCents,
          memoryGibHourCents: rate['memory_gib_hour'] ?? K8S_PAYG_DEFAULTS.memoryGibHourCents,
          storageGibMonthCents: rate['storage_gib_month'] ?? K8S_PAYG_DEFAULTS.storageGibMonthCents,
          egressGibCents: rate['egress_gib'] ?? K8S_PAYG_DEFAULTS.egressGibCents,
          baseFeeCents: svc.base_fee_cents
        }
      })
      .catch(() => {
        request = null
      })
    return request
  }

  return { rates, load }
})
